import React, { Suspense, Fragment, lazy } from "react"
import { Switch, Redirect, Route } from "react-router-dom"
import LoadingScreen from "components/LoadingScreen/LoadingScreen"
import ShopLayout from "layouts/shop"
import AdminLayout from "layouts/AdminLayout"
import BrandMindsLayout from "layouts/BrandMindsLayout"
import { adminRoutes } from "routes/admin.routes"
import AuthGuard from "components/AuthGuard"
import store from "store/index"
import { loadDynamoItemsBySK } from "store/modules/dataView.search"
import HelmetMetaData, { defaultPageTitle } from "components/HelmetMetaData"
import { myLazy } from "utils/myLazy"
import { campaignConfig, GoToCampaign, isCampaignPartiallyActive } from "views/shoppingCart/useCampaignInfo"
import { GooglePageViewTypes } from "utils/googleAnalytics/googleAnalyticsPageViewEvent"

store.dispatch(
  loadDynamoItemsBySK({ SK: "MAINTENANCE-SK", dictionary: "MAINTENANCE" })
)

const defaultmeta = [
  { property: "og:title", content: "Routes Title meta" },
  { property: "product:availability", content: "instock" }
]

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes?.map?.((route, i) => {
        const Guard = route.guard || Fragment
        const Layout = route.layout || Fragment
        const Component = route.component

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={(props) => {
              const guardProps = route.guard
                ? {path: props.location.pathname}
                : undefined

              return (
                <Guard {...guardProps}>
                  <HelmetMetaData {...route}/>
                  <Layout>
                    {route.routes ? (
                      renderRoutes(route.routes)
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )
            }}
          />
        )
      })}
    </Switch>
  </Suspense>
)

const routesObject = {
  // Main routes
  maintenance: {
    exact: true,
    path: "/maintenance",
    component: myLazy(() => import("views/MaintenancePage/MaintenancePage"))
  },
  
  admin: {
    path: "/admin",
    layout: AdminLayout,
    routes: [...adminRoutes || []],
    guard: AuthGuard
  },
  
  // Shop routes - each as a separate entry
  shopProductList: {
    exact: true,
    path: ["/shop/productlist", "/productlist"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.PRODUCT_LISTING,
    component: myLazy(() => import("views/productsList"))
  },
  
  shopDetailsWithCustomUrl: {
    exact: true,
    path: ["/shop/details/:customUrl", "/details/:customUrl"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.PRODUCT,
    component: myLazy(() => import("views/productDetails"))
  },
  
  shopDetails: {
    exact: true,
    path: ["/shop/details", "/details"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.PRODUCT,
    component: myLazy(() => import("views/productDetails"))
  },
  
  shopCart: {
    path: ["/shop/cart", "/cart"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CART,
    pageTitle: `${defaultPageTitle} - Coș de cumpărături`,
    component: myLazy(() => import("views/shoppingCart")),
    guard: AuthGuard
  },
  
  shopDashboard: {
    exact: true,
    path: ["/shop/dashboard", "/dashboard"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Dashboard`,
    component: myLazy(() => import("views/dashboard")),
    guard: AuthGuard
  },
  
  shopInvoices: {
    exact: true,
    path: ["/shop/invoices", "/invoices"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Facturi`,
    component: myLazy(() => import("views/invoices/index_2")),
    guard: AuthGuard
  },
  
  shopOrders: {
    exact: true,
    path: ["/shop/orders", "/orders"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Comenzi`,
    component: myLazy(() => import("views/orders")),
    guard: AuthGuard
  },

  shopReturns: {
    exact: true,
    path: ["/shop/returns", "/returns"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Retururi`,
    component: myLazy(() => import("views/returns")),
    guard: AuthGuard
  },
  
  shopReturnsDetails: {
    path: ["/shop/returns/details/:id", "/returns/details/:id"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Detalii retur`,
    component: myLazy(() => import("views/returns/ReturnDetails")),
    guard: AuthGuard
  },
  
  shopOrdersDetails: {
    path: ["/shop/orders/details", "/orders/details"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Detalii comandă`,
    component: myLazy(() => import("views/shoppingCart/orderDetails.js")),
    guard: AuthGuard
  },
  
  shopOffers: {
    exact: true,
    path: ["/shop/offers", "/offers"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Oferte`,
    component: myLazy(() => import("views/offerts")),
    guard: AuthGuard
  },
  
  shopOffersDetails: {
    path: ["/shop/offers/details", "/offers/details"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Detalii ofertă`,
    component: myLazy(() => import("views/offerts/offerDetails.jsx")),
    guard: AuthGuard
  },
  
  shopInvoiceDetails: {
    path: ["/shop/invoice/details", "/invoice/details"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Detalii factură`,
    component: myLazy(() => import("views/invoices/invoiceDetails")),
    guard: AuthGuard
  },
  
  shopLists: {
    exact: true,
    path: ["/shop/lists", "/lists"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Liste`,
    component: myLazy(() => import("views/lists/listsView")),
    guard: AuthGuard
  },
  
  shopNews: {
    exact: true,
    path: ["/shop/news", "/news"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.NEWS,
    pageTitle: `${defaultPageTitle} - Blog`,
    component: myLazy(() => import("views/guest/news/GuestCardsList"))
  },
  
  shopCareers: {
    exact: true,
    path: ["/shop/careers", "/careers"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Cariere`,
    component: myLazy(() => import("views/guest/news/GuestCardsList")),
    guard: AuthGuard
  },
  
  shopPredefinedLists: {
    exact: true,
    path: ["/shop/predefinedlists", "/predefinedlists"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Liste create de experții Uniprest Instal`,
    component: myLazy(() => import("views/guest/news/GuestCardsList"))
  },
  
  shopTutorials: {
    exact: true,
    path: ["/shop/tutorials", "/tutorials"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Tutoriale`,
    component: myLazy(() => import("views/guest/news/GuestCardsList")),
    guard: AuthGuard
  },
  
  shopAbout: {
    exact: true,
    path: ["/shop/about", "/about"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Despre noi`,
    component: myLazy(() => import("views/guest/about"))
  },
  
  shopArticle: {
    exact: false,
    path: ["/shop/article/:customUrl", "/article/:customUrl"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.BLOG,
    pageTitle: `${defaultPageTitle} - Articol`,
    component: myLazy(() => import("views/guest/news/GuestArticleDetails.jsx"))
  },
  
  shopUnilist: {
    exact: false,
    path: ["/shop/unilist/:customUrl", "/unilist/:customUrl"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - UniList`,
    component: myLazy(() => import("views/guest/news/GuestArticleDetails.jsx"))
  },
  
  shopTutorial: {
    exact: false,
    path: ["/shop/tutorial/:customUrl", "/tutorial/:customUrl"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Tutorial`,
    component: myLazy(() => import("views/guest/news/GuestArticleDetails.jsx")),
    guard: AuthGuard
  },
  
  shopJob: {
    exact: false,
    path: ["/shop/job/:customUrl", "/job/:customUrl"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Carieră`,
    component: myLazy(() => import("views/guest/news/GuestArticleDetails.jsx")),
    guard: AuthGuard
  },
  
  shopMyList: {
    exact: true,
    path: ["/shop/my-list", "/my-list"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Lista mea`,
    component: myLazy(() => import("views/guest/news/GuestArticleDetails.jsx"))
  },
  
  shopListsDetails: {
    exact: true,
    path: ["/shop/lists/details", "/lists/details"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Detalii listă`,
    component: myLazy(() => import("views/registeredUser/listDetails/index.jsx")),
    guard: AuthGuard
  },
  
  shopServices: {
    exact: true,
    path: ["/shop/services", "/services"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Servicii`,
    component: myLazy(() => import("views/guest/services"))
  },
  
  shopPartners: {
    exact: true,
    path: ["/shop/partners", "/partners"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Parteneri`,
    component: myLazy(() => import("views/guest/partners"))
  },
  
  shopMap: {
    exact: true,
    path: ["/shop/map", "/map"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Unde ne găsești`,
    component: myLazy(() => import("views/guest/map2"))
  },
  
  shopSupport: {
    exact: true,
    path: ["/shop/support", "/support"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Contact`,
    component: myLazy(() => import("views/guest/support"))
  },
  
  shopTerms: {
    exact: true,
    path: ["/shop/terms", "/terms"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: `${defaultPageTitle} - Termeni și condiții`,
    component: myLazy(() => import("views/guest/terms"))
  },
  
  shopSignup: {
    exact: true,
    path: ["/shop/signup", "/signup"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Solicită un cont`,
    component: myLazy(() => import("views/signUp/signUp.view")),
    guard: AuthGuard
  },
  
  shopSignin: {
    exact: true,
    path: ["/shop/signin", "/signin"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Login`,
    component: myLazy(() => import("views/signIn/signIn.view")),
    guard: AuthGuard
  },
  
  shopClientMycompany: {
    exact: true,
    path: ["/shop/client/mycompany", "/client/mycompany"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Compania mea`,
    component: myLazy(() => import("views/client/companiileMele/companiileMele")),
    guard: AuthGuard
  },
  
  shopClientUsers: {
    exact: true,
    path: ["/shop/client/users", "/client/users"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Utilizatori`,
    component: myLazy(() => import("views/client/utilizatori")),
    guard: AuthGuard
  },
  
  shopClientProfile: {
    exact: true,
    path: ["/shop/client/profile", "/client/profile"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Profilul meu`,
    component: myLazy(() => import("views/client/myProfile/myProfile")),
    guard: AuthGuard
  },
  
  shopAlerts: {
    exact: true,
    path: ["/shop/alerts", "/alerts"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Alerte`,
    component: myLazy(() => import("views/alert/Alert")),
    guard: AuthGuard
  },
  
  shopExcelimport: {
    exact: true,
    path: ["/shop/excelimport", "/excelimport"],
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Import Excel`,
    component: myLazy(() => import("views/ImportFromExcel/index.jsx")),
    guard: AuthGuard
  },
  
  shopNotFound: {
    layout: ShopLayout,
    googleAnalyticsPageType: GooglePageViewTypes.NOT_FOUND,
    pageTitle: `${defaultPageTitle} - Pagină negăsită`,
    component: myLazy(() => import("views/404Page/404Page"))
  },
  
  // Other routes
  devSorin: {
    exact: true,
    path: "/dev/sorin",
    component: myLazy(() => import("views/dev/sorin"))
  },
  
  devSorin3: {
    exact: true,
    path: "/dev/sorin3",
    component: myLazy(() => import("views/dev/sorin/index3"))
  },
  
  devSorin5: {
    exact: true,
    path: "/dev/sorin5",
    component: myLazy(() => import("views/dev/sorin/index5"))
  },
  
  devSorin6: {
    exact: true,
    path: "/dev/sorin6",
    component: myLazy(() => import("views/dev/sorin/index6"))
  },
  
  devGyulaToastr: {
    exact: true,
    path: "/dev/gyula/toastr",
    component: myLazy(() => import("views/dev/gyula/Toastr/index"))
  },
  
  home: {
    exact: true,
    path: "/",
    googleAnalyticsPageType: GooglePageViewTypes.HOME,
    pageTitle: "Uniprest",
    component: myLazy(() => import("views/homepage/HomepageInstantRedirect"))
  },
  
  devErno: {
    exact: true,
    path: "/dev/erno",
    component: myLazy(() => import("views/dev/erno"))
  },
  
  devErno2: {
    exact: true,
    path: "/dev/erno2",
    component: myLazy(() => import("views/dev/erno/index2"))
  },
  
  login: {
    exact: true,
    layout: ShopLayout,
    path: "/login",
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Login`,
    component: myLazy(() => import("views/signIn/signIn.view")),
    guard: AuthGuard
  },
  
  passwordReset: {
    exact: true,
    layout: ShopLayout,
    path: "/password-reset",
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Resetare parolă`,
    component: myLazy(() => import("views/passwordReset/passwordReset"))
  },
  
  invitationAccept: {
    exact: true,
    layout: ShopLayout,
    path: "/invitationaccept",
    googleAnalyticsPageType: GooglePageViewTypes.ACCOUNT,
    pageTitle: `${defaultPageTitle} - Acceptare invitație`,
    component: myLazy(() => import("views/passwordReset/passwordReset"))
  },
  
  devNorbert: {
    exact: true,
    path: "/dev/norbert",
    component: myLazy(() => import("views/dev/norbert"))
  },
  
  devTamas: {
    exact: true,
    path: "/dev/tamas",
    component: myLazy(() => import("views/dev/tamas"))
  },
  
  safariNotSupported: {
    exact: true,
    path: "/safariNotSupported",
    component: myLazy(() => import("views/MaintenancePage/MaintenancePage"))
  },
  
  notFound: {
    exact: true,
    layout: ShopLayout,
    path: "/404",
    googleAnalyticsPageType: GooglePageViewTypes.NOT_FOUND,
    pageTitle: `${defaultPageTitle} - Pagină negăsită`,
    component: myLazy(() => import("views/404Page/404Page"))
  },
  
  productDetails: {
    exact: true,
    layout: ShopLayout,
    path: "/:customUrl/pd/:productId",
    googleAnalyticsPageType: GooglePageViewTypes.PRODUCT,
    pageMeta: defaultmeta,
    component: myLazy(() => import("views/productDetails"))
  },
  
  categoryListing: {
    exact: true,
    layout: ShopLayout,
    path: "/:customUrl/ct",
    googleAnalyticsPageType: GooglePageViewTypes.PRODUCT_LISTING,
    component: myLazy(() => import("views/productsList"))
  },
  
  catchAll: {
    path: "*",
    layout: ShopLayout,
    routes: [
      {
        googleAnalyticsPageType: GooglePageViewTypes.NOT_FOUND,
        pageTitle: `${defaultPageTitle} - Pagină negăsită`,
        component: myLazy(() => import("views/404Page/404Page"))
      }
    ]
  },
  
  campaignLanding: {
    exact: true,
    path: "/un_viitor_digital_impreuna",
    googleAnalyticsPageType: GooglePageViewTypes.CONTENT,
    pageTitle: "Un viitor digital împreună",
    component: myLazy(() => import("views/campaignLanding/CampaignLandingPage"))
  }
}

let routes = [
  routesObject.maintenance,
  routesObject.admin,
  // Shop routes
  routesObject.shopProductList,
  routesObject.shopDetailsWithCustomUrl,
  routesObject.shopDetails,
  routesObject.shopCart,
  routesObject.shopDashboard,
  routesObject.shopInvoices,
  routesObject.shopOrders,
  routesObject.shopReturns,
  routesObject.shopReturnsDetails,
  routesObject.shopOrdersDetails,
  routesObject.shopOffers,
  routesObject.shopOffersDetails,
  routesObject.shopInvoiceDetails,
  routesObject.shopLists,
  routesObject.shopNews,
  routesObject.shopCareers,
  routesObject.shopPredefinedLists,
  routesObject.shopTutorials,
  routesObject.shopAbout,
  routesObject.shopArticle,
  routesObject.shopUnilist,
  routesObject.shopTutorial,
  routesObject.shopJob,
  routesObject.shopMyList,
  routesObject.shopListsDetails,
  routesObject.shopServices,
  routesObject.shopPartners,
  routesObject.shopMap,
  routesObject.shopSupport,
  routesObject.shopTerms,
  routesObject.shopSignup,
  routesObject.shopSignin,
  routesObject.shopClientMycompany,
  routesObject.shopClientUsers,
  routesObject.shopClientProfile,
  routesObject.shopAlerts,
  routesObject.shopExcelimport,
  // Other routes
  // routesObject.devSorin,
  // routesObject.devSorin3,
  // routesObject.devSorin5,
  // routesObject.devSorin6,
  // routesObject.devGyulaToastr,
  routesObject.home,
  // routesObject.devErno,
  // routesObject.devErno2,
  routesObject.login,
  routesObject.passwordReset,
  routesObject.invitationAccept,
  // routesObject.devNorbert,
  // routesObject.devTamas,
  routesObject.safariNotSupported,
  
  /*
  ----------------------------------------------
  REMOVED BLACK FRIDAY MEDIA REDIRECTS
  ----------------------------------------------
  {
    exact: true,
    path: "/media/black-friday.pdf",
    component: GoToCampaign
  },
  {
    exact: true,
    path: "/media/black-friday.html",
    component: GoToCampaign 
  },
  */

  routesObject.notFound,
  routesObject.productDetails,
  routesObject.categoryListing,
  routesObject.catchAll
]


const campaignLandingPageRoute = routesObject.campaignLanding



export default (
  isCampaignPartiallyActive() && campaignConfig.doesAffectRouting
)
  ? [campaignLandingPageRoute, ...routes || []]
  : routes
